import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { adminApi } from '../services/adminApi';

interface User {
  username: string;
  role: string;
}

interface AuthContextType {
  user: { username: string } | null;
  login: (username: string, password: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  mockSSOLogin: (username: string) => Promise<void>;
  hasGroupAccess: boolean;
  userGroups: any[];
  checkGroupAccess: (username?: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<{ username: string } | null>(null);
  const [hasGroupAccess, setHasGroupAccess] = useState<boolean>(false);
  const [userGroups, setUserGroups] = useState<any[]>([]);

  // Helper para exponer el usuario globalmente
  useEffect(() => {
    (window as any).auritasAuthContext = { user };
  }, [user]);

  // Función para verificar si el usuario tiene acceso a grupos
  const checkGroupAccess = async (username?: string): Promise<boolean> => {
    console.log('🔐 === INICIANDO checkGroupAccess ===');
    console.log('👤 Usuario actual:', user);
    console.log('📧 Username recibido como parámetro:', username);
    
    const userToCheck = username || user?.username;
    
    if (!userToCheck) {
      console.log('❌ No hay usuario para verificar, retornando false');
      return false;
    }
    
    // El admin tiene acceso automático sin necesidad de estar en un grupo
    if (userToCheck === 'admin') {
      console.log('👑 Usuario es admin, acceso automático');
      setUserGroups([{ id: 0, name: 'Administrator', permissions: [] }]);
      setHasGroupAccess(true);
      return true;
    }
    
    console.log('🔍 Usuario no es admin, verificando grupos...');
    
    try {
      const permissionsData = await getUserPermissions(userToCheck);
      console.log('📊 Datos de permisos obtenidos:', permissionsData);
      
      if (permissionsData && permissionsData.groups && permissionsData.groups.length > 0) {
        console.log('✅ Usuario tiene grupos asignados');
        setUserGroups(permissionsData.groups);
        setHasGroupAccess(true);
        return true;
      } else {
        console.log('❌ Usuario no tiene grupos asignados');
        setUserGroups([]);
        setHasGroupAccess(false);
        return false;
      }
    } catch (error) {
      console.error('❌ Error checking group access:', error);
      setUserGroups([]);
      setHasGroupAccess(false);
      return false;
    }
  };

  // Función temporal de debug para verificar la API
  const debugApiResponse = async () => {
    try {
      console.log('🔍 === DEBUG API RESPONSE ===');
      
      // 1. Obtener usuarios
      const usersResponse = await adminApi.getUsers();
      console.log('📊 Users API Response:', usersResponse);
      
      // 2. Obtener grupos
      const groupsResponse = await adminApi.getGroups();
      console.log('📊 Groups API Response:', groupsResponse);
      
      // 3. Verificar estructura de grupos
      if (groupsResponse.success && groupsResponse.data) {
        console.log('📊 Groups structure:');
        groupsResponse.data.forEach((group: any, index: number) => {
          console.log(`   Grupo ${index + 1}:`, {
            id: group.id,
            name: group.name,
            hasUsers: !!group.users,
            usersCount: group.users ? group.users.length : 'NO USERS PROPERTY',
            hasPermissions: !!group.permissions,
            permissionsCount: group.permissions ? group.permissions.length : 'NO PERMISSIONS PROPERTY'
          });
        });
      }
      
      console.log('🔍 === END DEBUG ===');
    } catch (error) {
      console.error('❌ Debug error:', error);
    }
  };

  // Función para obtener y mostrar los permisos del usuario
  const getUserPermissions = async (userEmail: string) => {
    console.log('🚀 === INICIANDO getUserPermissions ===');
    console.log('📧 Email recibido:', userEmail);
    
    try {
      console.log('🔍 Obteniendo permisos para usuario:', userEmail);
      
      // 1. Obtener todos los usuarios para encontrar el ID del usuario actual
      const usersResponse = await adminApi.getUsers();
      if (!usersResponse.success) {
        console.error('❌ Error obteniendo usuarios:', usersResponse);
        return;
      }
      
      console.log('📊 Todos los usuarios disponibles:', usersResponse.data);
      console.log('🔍 Buscando usuario con email:', userEmail);
      console.log('🔍 Tipo de dato del email buscado:', typeof userEmail);
      
      const currentUser = usersResponse.data.find((user: any) => {
        console.log(`   Comparando: "${user.email}" (${typeof user.email}) vs "${userEmail}" (${typeof userEmail})`);
        const match = user.email === userEmail;
        console.log(`   ¿Coincide? ${match}`);
        return match;
      });
      
      if (!currentUser) {
        console.error('❌ Usuario no encontrado:', userEmail);
        console.log('📋 Usuarios disponibles:', usersResponse.data.map((u: any) => u.email));
        return;
      }
      
      console.log('👤 Usuario encontrado:', currentUser);
      console.log('🆔 ID del usuario encontrado:', currentUser.id);
      
      // 2. Obtener todos los grupos con sus usuarios y permisos
      const groupsResponse = await adminApi.getGroups();
      if (!groupsResponse.success) {
        console.error('❌ Error obteniendo grupos:', groupsResponse);
        return;
      }
      
      console.log('📊 Todos los grupos obtenidos:', groupsResponse.data);
      
      // 3. Encontrar los grupos a los que pertenece el usuario
      const userGroups = groupsResponse.data.filter((group: any) => {
        console.log(`🔍 Verificando grupo "${group.name}":`, group);
        console.log(`👥 Usuarios en este grupo:`, group.users);
        console.log(`🆔 Buscando usuario con ID: ${currentUser.id}`);
        
        const belongsToGroup = group.users.some((user: any) => {
          console.log(`   Comparando: user.id=${user.id} (${typeof user.id}) vs currentUser.id=${currentUser.id} (${typeof currentUser.id})`);
          const match = user.id === currentUser.id;
          console.log(`   ¿Pertenece al grupo "${group.name}"? ${match}`);
          return match;
        });
        
        console.log(`✅ Usuario ${userEmail} pertenece al grupo "${group.name}": ${belongsToGroup}`);
        return belongsToGroup;
      });
      
      console.log('👥 Grupos del usuario:', userGroups);
      console.log('🔢 Total de grupos encontrados:', userGroups.length);
      
      // 4. Recolectar todos los permisos únicos de todos los grupos del usuario
      const userPermissions = new Set();
      const permissionsDetails: any[] = [];
      
      userGroups.forEach((group: any) => {
        group.permissions.forEach((permission: any) => {
          if (!userPermissions.has(permission.id)) {
            userPermissions.add(permission.id);
            permissionsDetails.push({
              ...permission,
              grantedByGroup: group.name
            });
          }
        });
      });
      
      // 5. Mostrar resumen completo en consola
      console.log('🚀 === RESUMEN DE PERMISOS DEL USUARIO ===');
      console.log('👤 Usuario:', userEmail);
      console.log('🆔 ID del usuario:', currentUser.id);
      console.log('👥 Grupos a los que pertenece:', userGroups.map((g: any) => g.name));
      console.log('🔢 Total de grupos:', userGroups.length);
      console.log('🔐 Permisos únicos:', Array.from(userPermissions));
      console.log('📋 Detalle de permisos:');
      
      permissionsDetails.forEach((permission, index) => {
        console.log(`   ${index + 1}. ${permission.name} (${permission.description}) - Otorgado por grupo: ${permission.grantedByGroup}`);
      });
      
      console.log('🔢 Total de permisos únicos:', permissionsDetails.length);
      console.log('==========================================');
      
      // 6. Mostrar permisos por grupo
      console.log('📊 PERMISOS POR GRUPO:');
      userGroups.forEach((group: any) => {
        console.log(`\n🏷️  Grupo: ${group.name} (ID: ${group.id})`);
        if (group.permissions.length === 0) {
          console.log('   ❌ Sin permisos asignados');
        } else {
          group.permissions.forEach((permission: any, index: number) => {
            console.log(`   ${index + 1}. ${permission.name} - ${permission.description}`);
          });
        }
      });
      
      return {
        user: currentUser,
        groups: userGroups,
        permissions: permissionsDetails,
        totalPermissions: permissionsDetails.length
      };
      
    } catch (error) {
      console.error('❌ Error obteniendo permisos del usuario:', error);
    }
  };

  const login = async (username: string, password: string) => {
    try {
      // Get all users and check if the user exists
      const usersResponse = await adminApi.getUsers();
      
      if (usersResponse.success) {
        const existingUser = usersResponse.data.find((user: any) => user.email === username);
        
        if (existingUser) {
          // User exists, proceed with login
          console.log('User found:', existingUser);
          setUser({ username });
          (window as any).auritasAuthContext = { user: { username } };
          
          // Debug API response
          await debugApiResponse();
          
          // Verificar acceso a grupos después del login
          const hasAccess = await checkGroupAccess(username);
          
          if (!hasAccess && username !== 'admin') {
            // alert('Usuario satisfactoriamente logueado pero no tenemos un grupo asignado para ti. Por favor contacta a tu admin.');
            console.log('⚠️ Usuario sin grupo asignado, pero no cerrando sesión para debug');
            // logout(); // Cerrar sesión si no tiene grupo
            return { success: false, message: 'No group access. Please contact administrator.' };
          }
          
          return { success: true };
        } else {
          // User doesn't exist, create new user
          console.log('User not found, creating new user:', username);
          const createResponse = await adminApi.createUser({ email: username });
          
          if (createResponse.success) {
            console.log('New user created:', createResponse.data);
            
            setUser({ username });
            (window as any).auritasAuthContext = { user: { username } };
            
            // Verificar acceso a grupos después de crear el usuario
            const hasAccess = await checkGroupAccess(username);
            
            if (!hasAccess && username !== 'admin') {
              // alert('Usuario satisfactoriamente logueado pero no tenemos un grupo asignado para ti. Por favor contacta a tu admin.');
              console.log('⚠️ Usuario sin grupo asignado, pero no cerrando sesión para debug');
              // logout(); // Cerrar sesión si no tiene grupo
              return { success: false, message: 'No group access. Please contact administrator.' };
            }
            
            return { 
              success: true, 
              message: 'Welcome! Your account has been created. Please contact an administrator to assign you to a group and grant access permissions.' 
            };
          } else {
            console.error('Failed to create user:', createResponse);
            return { success: false, message: 'Failed to create user account.' };
          }
        }
      } else {
        console.error('Failed to fetch users:', usersResponse);
        return { success: false, message: 'Failed to check existing users.' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'An unexpected error occurred during login.' };
    }
  };

  const logout = () => {
    setUser(null);
    setHasGroupAccess(false);
    setUserGroups([]);
    (window as any).auritasAuthContext = { user: null };
  };

  const mockSSOLogin = async (username: string) => {
    setUser({ username });
    (window as any).auritasAuthContext = { user: { username } };
    
    // Verificar acceso a grupos después del login mock
    const hasAccess = await checkGroupAccess(username);
    
    if (!hasAccess && username !== 'admin') {
      // alert('Usuario satisfactoriamente logueado pero no tenemos un grupo asignado para ti. Por favor contacta a tu admin.');
      console.log('⚠️ Usuario sin grupo asignado, pero no cerrando sesión para debug');
      // logout(); // Cerrar sesión si no tiene grupo
      return;
    }
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    mockSSOLogin,
    hasGroupAccess,
    userGroups,
    checkGroupAccess
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Drawer context for global menu control
interface DrawerContextType {
  drawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
}

const DrawerContext = createContext<DrawerContextType | undefined>(undefined);

export const DrawerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const openDrawer = () => setDrawerOpen(true);
  const closeDrawer = () => setDrawerOpen(false);
  return (
    <DrawerContext.Provider value={{ drawerOpen, openDrawer, closeDrawer }}>
      {children}
    </DrawerContext.Provider>
  );
};

export const useDrawer = () => {
  const ctx = useContext(DrawerContext);
  if (!ctx) throw new Error('useDrawer must be used within a DrawerProvider');
  return ctx;
}; 